import type { LngLat, Route, RouteStep } from '../shared/types';
import type { PositionSample } from '../sensors/position';
import { cumulativeDistances } from './geo';
import { sectionAt, type FacilityKind, type HighwayPlan } from './highway';
import { laneHint, type LaneGuide } from './lanes';
import { OffRouteDetector } from './offRoute';
import { RouteSnapper } from './snap';

export interface NavState {
  raw: PositionSample;
  /** ルート上の位置（ルート未設定時は生の位置） */
  position: LngLat;
  /** 表示に使う進行方向 [度] */
  bearing: number;
  /** ルートからのずれ [m]（ルート未設定時は 0） */
  offset: number;
  /** 残り距離 [m] */
  remaining?: number;
  /** 残りの所要時間 [s]（プロバイダの所要時間から） */
  remainingTime?: number;
  /** 次の案内 */
  nextStep?: RouteStep;
  /** 次の案内地点までの距離 [m] */
  distanceToNextStep?: number;
  arrived: boolean;
  /** ルートから大きく外れている（表示は生の位置。再探索待ち） */
  offRoute: boolean;
  /** 高速道路を走行中のときだけ入る */
  highway?: HighwayState;
  /** 次の案内地点の車線案内（分岐の 2.5km 手前から） */
  lanes?: LaneGuide;
}

export interface HighwayState {
  /** これから通る施設（近い順、最大4件） */
  upcoming: { kind: FacilityKind; name: string; distance: number; eta: number }[];
  /** 降りる IC（分からなければ区間の終わり） */
  exit: { name?: string; distance: number; eta: number };
}

export interface NavigatorEvents {
  onState(state: NavState): void;
  /** ルート逸脱。呼び出し側で再探索する */
  onOffRoute(position: LngLat): void;
  /** 音声案内のタイミング */
  onAnnounce(text: string): void;
}

/** 案内を読み上げる距離 [m]（遠い順） */
const ANNOUNCE_AT = [700, 300, 80];
/** 高速道路上の案内。100km/h だと 700m は約25秒前にしかならないので早めにする */
const HIGHWAY_ANNOUNCE_AT = [2000, 1000, 400];
/** 車線案内を出し始める距離 [m] */
const LANES_FROM = 2500;
/** 施設リストに出す件数 */
const UPCOMING_COUNT = 4;
const ARRIVE_RADIUS = 30;
/** ルートに乗る前、始点から離れていくと判断する余裕 [m] */
const APPROACH_MARGIN = 100;

/**
 * ナビゲーションの状態機械（ロジックのみ。描画や音声には依存しない）。
 * 位置 → スナップ → 逸脱判定 → 次の案内の算出 → イベント通知。
 */
export class Navigator {
  private route: Route | null = null;
  private snapper: RouteSnapper | null = null;
  private stepAlong: number[] = [];
  /** 案内地点ごとの、始点からの所要時間 [s]（到着予想時刻の計算用） */
  private stepTime: number[] = [];
  private plan: HighwayPlan | null = null;
  private readonly offRoute = new OffRouteDetector();
  private announced = new Set<string>();
  private lastHeading = 0;
  /** 一度ルートに乗ったか。出発地（駐車場・施設内など）は道路から離れているのが普通なので、乗るまでは逸脱扱いしない */
  private joined = false;
  /** ルートに乗る前の、最初のずれ [m] */
  private startOffset = Number.NaN;

  constructor(private readonly ev: NavigatorEvents) {}

  /** plan: 高速道路の区間と施設（高速を通らないルートや、施設データが無いときは null） */
  setRoute(route: Route | null, plan: HighwayPlan | null = null): void {
    this.route = route;
    this.plan = plan?.sections.length ? plan : null;
    this.announced.clear();
    this.offRoute.reset();
    this.joined = false;
    this.startOffset = Number.NaN;
    if (!route) {
      this.snapper = null;
      this.stepAlong = [];
      return;
    }
    this.snapper = new RouteSnapper(route.geometry);
    const cum = cumulativeDistances(route.geometry);
    this.stepAlong = route.steps.map((s) => cum[Math.min(s.startIndex, cum.length - 1)]);
    this.stepTime = [0];
    for (const s of route.steps) this.stepTime.push(this.stepTime[this.stepTime.length - 1] + s.duration);
  }

  /** 始点から along [m] 地点までの所要時間 [s]（プロバイダの所要時間を案内地点の間で按分） */
  private timeAt(along: number): number {
    const a = this.stepAlong;
    if (!a.length) return 0;
    let i = 0;
    while (i < a.length - 1 && a[i + 1] <= along) i++;
    const len = (a[i + 1] ?? this.snapper!.totalDistance) - a[i];
    const f = len > 0 ? Math.min(1, (along - a[i]) / len) : 0;
    return this.stepTime[i] + (this.stepTime[i + 1] - this.stepTime[i]) * f;
  }

  private highwayState(along: number, now: number): HighwayState | undefined {
    const section = this.plan && sectionAt(this.plan, along);
    if (!section) return undefined;
    const t0 = this.timeAt(along);
    const eta = (at: number) => now + (this.timeAt(at) - t0) * 1000;
    const ahead = this.plan!.facilities.filter((f) => f.along > along + 10 && f.along <= section.end + 300);
    const exitAt = ahead.find((f) => f.name === section.exit)?.along ?? section.end;
    return {
      // 降りる IC は「出口」の行に出すので、リストからは外す
      upcoming: ahead
        .filter((f) => f.name !== section.exit)
        .slice(0, UPCOMING_COUNT).map((f) => ({ kind: f.kind, name: f.name, distance: f.along - along, eta: eta(f.along) })),
      exit: { name: section.exit, distance: Math.max(0, exitAt - along), eta: eta(exitAt) },
    };
  }

  get hasRoute(): boolean {
    return this.route !== null;
  }

  update(sample: PositionSample): void {
    // 進行方向: GPS の heading（移動中のみ有効）→ 前回値
    if (sample.heading != null && !Number.isNaN(sample.heading) && (sample.speed ?? 0) > 1) {
      this.lastHeading = sample.heading;
    }

    if (!this.route || !this.snapper) {
      this.ev.onState({
        raw: sample,
        position: sample.position,
        bearing: this.lastHeading,
        offset: 0,
        arrived: false,
        offRoute: false,
      });
      return;
    }

    const snap = this.snapper.snap(sample.position);
    // ルートから大きく外れているときは線上に吸い寄せず、実際の位置と向きで表示する
    // （吸い寄せたままだと、遠く離れたルート上に自車が張り付いて現在地に戻らない）
    const away = this.offRoute.exceeds(snap.offset, sample.accuracy);

    // ルートに乗る前は、始点から離れていく場合だけ逸脱とみなす（近づいている間は再探索しない）
    if (!this.joined) {
      if (!away) this.joined = true;
      else if (Number.isNaN(this.startOffset)) this.startOffset = snap.offset;
    }
    const approaching = !this.joined && snap.offset < this.startOffset + APPROACH_MARGIN;
    if (this.offRoute.update(approaching ? 0 : snap.offset, sample.accuracy)) {
      this.ev.onOffRoute(sample.position);
    }
    const off = away && !approaching;

    const remaining = this.snapper.totalDistance - snap.along;
    // ルートから離れているときは、線上の最寄り点が終点でも到着とみなさない
    const arrived = remaining < ARRIVE_RADIUS && !away;

    // 次のステップ = 現在地より先で最初に始まるステップ
    let nextIdx = this.stepAlong.findIndex((a) => a > snap.along + 1);
    if (nextIdx === -1) nextIdx = this.route.steps.length - 1;
    const nextStep = this.route.steps[nextIdx];
    const distanceToNextStep = Math.max(0, (this.stepAlong[nextIdx] ?? this.snapper.totalDistance) - snap.along);

    // 次の案内地点が高速区間の中なら、高速用の早めのタイミングで読み上げる
    const onHighwayStep = !!this.plan && !!sectionAt(this.plan, this.stepAlong[nextIdx] ?? snap.along, 0);
    // 外れている間は古いルートの案内を読み上げない（再探索の結果を待つ）。高速上の「車線を選ぶだけ」の地点も読み上げない
    const lanes = this.plan?.guides.get(nextIdx);
    if (!off && !this.plan?.quietSteps.has(nextIdx)) {
      this.maybeAnnounce(nextIdx, nextStep, distanceToNextStep, arrived, onHighwayStep ? HIGHWAY_ANNOUNCE_AT : ANNOUNCE_AT, lanes ? laneHint(lanes) : '');
    }

    this.ev.onState({
      raw: sample,
      position: away ? sample.position : snap.point,
      bearing: away ? this.lastHeading : snap.bearing,
      offset: snap.offset,
      remaining,
      remainingTime: Math.max(0, this.timeAt(this.snapper.totalDistance) - this.timeAt(snap.along)),
      nextStep,
      distanceToNextStep,
      arrived,
      offRoute: off,
      highway: off ? undefined : this.highwayState(snap.along, Date.now()),
      lanes: !off && lanes && distanceToNextStep <= LANES_FROM ? lanes : undefined,
    });
  }

  /** hint: 読み上げに添える車線の一言（「左側の車線を進んでください」） */
  private maybeAnnounce(idx: number, step: RouteStep | undefined, dist: number, arrived: boolean, thresholds: number[], hint = '') {
    if (arrived) {
      if (!this.announced.has('arrive')) {
        this.announced.add('arrive');
        this.ev.onAnnounce('目的地に到着しました。');
      }
      return;
    }
    if (!step) return;
    // 今の距離に当てはまる、いちばん近いしきい値で1回だけ読み上げる。
    // それより遠いしきい値は通り過ぎたものとして既読にする（以前は遠い側で読み上げ、次の更新で近い側でも読み上げて連呼していた）
    const passed = thresholds.filter((t) => dist <= t);
    if (!passed.length) return;
    const th = Math.min(...passed);
    if (this.announced.has(`${idx}@${th}`)) return;
    passed.forEach((t) => this.announced.add(`${idx}@${t}`));
    // 100m 未満は「およそ30メートル先」ではなく「まもなく」と言う
    const lead = th >= 100 && dist >= 100 ? `およそ${roundDistance(dist)}先、` : 'まもなく、';
    this.ev.onAnnounce(lead + step.instruction + (hint ? `。${hint}` : ''));
  }
}

export function roundDistance(m: number): string {
  // 「2.0キロ」とは読ませない
  if (m >= 1000) return `${(m / 1000).toFixed(m >= 10000 ? 0 : 1).replace(/\.0$/, '')}キロ`;
  if (m >= 100) return `${Math.round(m / 50) * 50}メートル`;
  return `${Math.round(m / 10) * 10}メートル`;
}
